/**
 * UserPickerModal — Sprint 27 Phase 4.
 *
 * Replaces the hot-fix `window.prompt()` with a proper searchable
 * Combobox of active Odoo internal users. Used by the Assign button
 * on the triage card + (future) the alerts banner.
 *
 * Data source: `useAssignableUsers()` (5-min cached) → backed by
 * `GET /api/maildesk-triage/users` → Odoo `res.users` filtered to
 * active + non-share (internal users only).
 */

import { useMemo, useState } from 'react'
import { Combobox, Modal } from '@/components/ui'
import type { ComboboxItem } from '@/components/ui'
import { useAssignableUsers } from '../api/queries'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-filled label (e.g. customer / item title) for the modal header. */
  contextLabel?: string
  /** Called with the chosen Odoo res.users.id when the operator picks. */
  onPick: (userId: number, userName: string) => void
}

export function UserPickerModal({
  open,
  onOpenChange,
  contextLabel,
  onPick,
}: Props) {
  // Don't poll the endpoint until the modal is open — saves an Odoo
  // round-trip on every page load.
  const usersQ = useAssignableUsers(open)
  const [value, setValue] = useState<string>('')

  const items = useMemo<ComboboxItem[]>(() => {
    const raw = usersQ.data?.users ?? []
    return raw.map((u) => ({
      value: String(u.id),
      label: u.name,
      meta: u.email ?? undefined,
      keywords: [u.name, u.email ?? ''],
    }))
  }, [usersQ.data])

  const handleChange = (v: string) => {
    setValue(v)
    const picked = usersQ.data?.users.find((u) => String(u.id) === v)
    if (picked) {
      onPick(picked.id, picked.name)
      onOpenChange(false)
      setValue('')
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Assign to"
      description={
        contextLabel ? `Routing: ${contextLabel}` : 'Pick an SPI user.'
      }
      footer={null}
    >
      <div className="min-h-[280px]">
        {usersQ.isLoading ? (
          <p className="text-role-metadata">Loading users…</p>
        ) : usersQ.isError ? (
          <p className="text-role-metadata">Couldn't load users. Try again.</p>
        ) : (
          <Combobox
            items={items}
            value={value}
            onChange={handleChange}
            placeholder="Search by name or email…"
            emptyMessage="No matching users."
          />
        )}
      </div>
    </Modal>
  )
}
